/**
 * Email Verification Page
 *
 * Enhanced with:
 * - oobCode processing from URL (handles direct links and redirects)
 * - 60-second resend cooldown with countdown
 * - Structured error display with recovery actions
 * - "Request Admin Verification" escape hatch after 3+ failed resends
 * - Auto-polling as cross-tab/cross-browser fallback
 * - fromLogin detection for contextual messaging
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { auth, db, functions } from '../lib/firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { isPlatformAdmin } from '../lib/platformAdmin';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Mail, RefreshCw, LogOut, CheckCircle, Loader2, ShieldCheck, AlertTriangle, Clock } from 'lucide-react';
import { AuthCardTitle, AuthShell } from '../components/auth/AuthShell';
import { getChatGPTConsentReturnTo } from '../lib/chatgptConsent';

const COOLDOWN_KEY = 'verify-email-cooldown-until';
const COOLDOWN_SECONDS = 60;
const RESEND_COUNT_KEY = 'verify-email-resend-count';
const PAGE_VISIT_KEY = 'verify-email-first-visit';

export const VerifyEmail: React.FC = () => {
  const { user, sendVerificationEmail, reloadUser, verifyEmailWithCode } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const returnTo = getChatGPTConsentReturnTo(searchParams.get('returnTo'));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [processingCode, setProcessingCode] = useState(false);

  // Cooldown state
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  // Admin request state
  const [resendCount, setResendCount] = useState(() => {
    return parseInt(localStorage.getItem(RESEND_COUNT_KEY) || '0', 10);
  });
  const [adminRequestSent, setAdminRequestSent] = useState(false);
  const [sendingAdminRequest, setSendingAdminRequest] = useState(false);

  // Track time on page for showing admin request
  const [minutesOnPage, setMinutesOnPage] = useState(0);

  // Detect fromLogin navigation state
  const fromLogin = (location.state as any)?.fromLogin === true;

  // Show admin request button after 3+ resend attempts OR 10+ minutes on page
  const showAdminRequest = (resendCount >= 3 || minutesOnPage >= 10) && !adminRequestSent;

  // --- oobCode processing from URL ---
  useEffect(() => {
    const oobCode = searchParams.get('oobCode');
    const mode = searchParams.get('mode');

    if (oobCode && mode === 'verifyEmail') {
      processOobCode(oobCode);
    }
  }, [searchParams]);

  async function processOobCode(oobCode: string) {
    setProcessingCode(true);
    setError('');
    setMessage('');
    try {
      await verifyEmailWithCode(oobCode);
      setMessage('Email verified successfully! Redirecting...');
      setTimeout(() => navigate(returnTo, { replace: true }), 2000);
    } catch (err: any) {
      console.error('[VerifyEmail] oobCode processing failed:', err.code);
      if (err.code === 'auth/invalid-action-code') {
        // Check if already verified
        if (auth.currentUser) {
          await auth.currentUser.reload();
          if (auth.currentUser.emailVerified) {
            await reloadUser();
            setMessage('Email already verified! Redirecting...');
            setTimeout(() => navigate(returnTo, { replace: true }), 2000);
            return;
          }
        }
        setError('This verification link has already been used or is invalid. Please request a new one.');
      } else if (err.code === 'auth/expired-action-code') {
        setError('This verification link has expired. Please click "Resend" below to get a new one.');
      } else {
        setError('Could not verify your email. Please try resending the verification email.');
      }
    } finally {
      setProcessingCode(false);
    }
  }

  // --- Redirect if already verified ---
  useEffect(() => {
    if (user?.emailVerified) {
      navigate(returnTo);
    }
  }, [user, navigate, returnTo]);

  // --- Auto-poll for verification every 5 seconds ---
  useEffect(() => {
    if (!user || user.emailVerified) return;

    const interval = setInterval(async () => {
      try {
        if (auth.currentUser) {
          await auth.currentUser.reload();
          if (auth.currentUser.emailVerified) {
            await reloadUser();
            navigate(returnTo);
          }
        }
      } catch {
        // Silently ignore polling errors
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [user, reloadUser, navigate, returnTo]);

  // --- Cooldown timer ---
  useEffect(() => {
    function checkCooldown() {
      const cooldownUntil = parseInt(localStorage.getItem(COOLDOWN_KEY) || '0', 10);
      const now = Date.now();
      if (cooldownUntil > now) {
        setCooldownRemaining(Math.ceil((cooldownUntil - now) / 1000));
      } else {
        setCooldownRemaining(0);
      }
    }

    checkCooldown();
    const interval = setInterval(checkCooldown, 1000);
    return () => clearInterval(interval);
  }, []);

  // --- Track time on page ---
  useEffect(() => {
    const firstVisit = localStorage.getItem(PAGE_VISIT_KEY);
    if (!firstVisit) {
      localStorage.setItem(PAGE_VISIT_KEY, Date.now().toString());
    }

    const interval = setInterval(() => {
      const visitTime = parseInt(localStorage.getItem(PAGE_VISIT_KEY) || Date.now().toString(), 10);
      setMinutesOnPage(Math.floor((Date.now() - visitTime) / 60000));
    }, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, []);

  // --- Resend with cooldown ---
  const handleResendEmail = async () => {
    if (cooldownRemaining > 0) return;

    setError('');
    setMessage('');
    setLoading(true);

    try {
      if (user) {
        await sendVerificationEmail(user);
        setMessage('Verification email sent! Please check your inbox (and spam/junk folder).');

        // Start cooldown
        const cooldownUntil = Date.now() + COOLDOWN_SECONDS * 1000;
        localStorage.setItem(COOLDOWN_KEY, cooldownUntil.toString());
        setCooldownRemaining(COOLDOWN_SECONDS);

        // Track resend count
        const newCount = resendCount + 1;
        setResendCount(newCount);
        localStorage.setItem(RESEND_COUNT_KEY, newCount.toString());
      }
    } catch (err: any) {
      console.error('Error sending verification email:', err);
      if (err.code === 'auth/too-many-requests') {
        setError('Too many requests. Please wait a few minutes before trying again.');
      } else {
        setError('Failed to send verification email. Please try again later.');
      }

      // Track failed resend count too
      const newCount = resendCount + 1;
      setResendCount(newCount);
      localStorage.setItem(RESEND_COUNT_KEY, newCount.toString());
    } finally {
      setLoading(false);
    }
  };

  // --- Manual verification check ---
  const handleCheckVerification = useCallback(async () => {
    setError('');
    setVerifying(true);

    try {
      if (auth.currentUser) {
        await auth.currentUser.reload();
      }
      if (auth.currentUser?.emailVerified) {
        await reloadUser();
        navigate(returnTo);
      } else {
        setError('Email not verified yet. Please check your inbox and click the verification link. Also check your spam/junk folder.');
      }
    } catch (err: any) {
      console.error('Error checking verification:', err);
      setError('Failed to check verification status. Please try again.');
    } finally {
      setVerifying(false);
    }
  }, [reloadUser, navigate, returnTo]);

  // --- Admin verification request ---
  const handleAdminRequest = async () => {
    if (!user) return;
    setSendingAdminRequest(true);

    try {
      // If current user IS an admin, directly call the Cloud Function
      if (isPlatformAdmin(user.email)) {
        const adminVerify = httpsCallable(functions, 'adminVerifyEmail');
        await adminVerify({ targetUid: user.uid });
        await reloadUser();
        setMessage('Account verified! Redirecting...');
        setAdminRequestSent(true);
        setTimeout(() => navigate(returnTo, { replace: true }), 2000);
        return;
      }

      // For non-admin users: write a request to Firestore
      await setDoc(doc(db, 'verificationRequests', user.uid), {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || user.email?.split('@')[0] || 'Unknown',
        requestedAt: serverTimestamp(),
        status: 'pending',
        resendAttempts: resendCount,
        minutesOnPage,
      });

      setAdminRequestSent(true);
      setMessage('Your verification request has been sent to the platform admin. You will receive an email when your account is verified.');
    } catch (err: any) {
      console.error('Error requesting admin verification:', err);
      setError('Failed to send admin request. Please try again or contact support directly.');
    } finally {
      setSendingAdminRequest(false);
    }
  };

  return (
    <AuthShell>
      <Card className="w-full border-border bg-card shadow-[0_15px_35px_-5px_rgba(0,0,0,0.15)]">
        <CardHeader className="space-y-3">
          <div className="mb-1 flex h-12 w-12 items-center justify-center bg-muted text-primary">
            {processingCode ? (
              <Loader2 className="h-6 w-6 animate-spin motion-reduce:animate-none" />
            ) : (
              <Mail className="h-6 w-6" />
            )}
          </div>
          <AuthCardTitle className="text-3xl font-normal text-foreground">
            {processingCode ? 'Verifying...' : 'Verify your email'}
          </AuthCardTitle>
          <CardDescription className="text-base">
            {processingCode ? (
              'Processing your verification link...'
            ) : fromLogin ? (
              <>
                Your email hasn&apos;t been verified yet. Please check your inbox for <br />
                <span className="font-medium text-foreground">{user?.email}</span>
              </>
            ) : (
              <>
                We sent a verification email to <br />
                <span className="font-medium text-foreground">{user?.email}</span>
              </>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {message && (
            <Alert className="border-status-success-foreground/20 bg-status-success text-status-success-foreground">
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          )}

          {!processingCode && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Click the link in the email to verify your account. If you can&apos;t find the email, check your <strong>spam</strong>, <strong>junk</strong>, <strong>promotions</strong>, or <strong>updates</strong> folders. This page will automatically detect when you&apos;ve verified.
              </p>

              <Button
                onClick={handleCheckVerification}
                className="w-full h-11 font-medium shadow-md hover:shadow-lg transition-smooth"
                disabled={verifying}
              >
                {verifying ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
                    Checking...
                  </>
                ) : (
                  "I've Verified My Email"
                )}
              </Button>

              <Button
                variant="outline"
                onClick={handleResendEmail}
                className="w-full h-11 font-medium"
                disabled={loading || cooldownRemaining > 0}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
                    Sending...
                  </>
                ) : cooldownRemaining > 0 ? (
                  <>
                    <Clock className="mr-2 h-4 w-4" />
                    Resend available in {cooldownRemaining}s
                  </>
                ) : (
                  'Resend Verification Email'
                )}
              </Button>

              {/* Admin verification request - shows after 3+ resends or 10+ minutes */}
              {showAdminRequest && (
                <div className="pt-2 border-t border-border">
                  <p className="mb-3 text-xs text-muted-foreground">
                    Still having trouble? Request manual verification from a platform administrator.
                  </p>
                  <Button
                    variant="outline"
                    onClick={handleAdminRequest}
                    className="h-11 w-full border-status-warning-foreground/30 font-medium text-status-warning-foreground hover:bg-status-warning"
                    disabled={sendingAdminRequest || adminRequestSent}
                  >
                    {sendingAdminRequest ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
                        Sending Request...
                      </>
                    ) : adminRequestSent ? (
                      <>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Request Sent
                      </>
                    ) : (
                      <>
                        <ShieldCheck className="mr-2 h-4 w-4" />
                        Request Admin Verification
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}

          <div className="pt-4 border-t border-border text-center">
            <LogoutButton />
          </div>
        </CardContent>
      </Card>
    </AuthShell>
  );
};

/**
 * Separate logout button component to properly use the useAuth hook
 */
const LogoutButton: React.FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      localStorage.removeItem(COOLDOWN_KEY);
      localStorage.removeItem(RESEND_COUNT_KEY);
      localStorage.removeItem(PAGE_VISIT_KEY);
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Failed to log out', err);
    }
  };

  return (
    <Button variant="ghost" size="sm" onClick={handleLogout} className="text-muted-foreground">
      <LogOut className="mr-2 h-4 w-4" />
      Log Out
    </Button>
  );
};
